import { Children, isValidElement } from "react";
import type { ComponentType, ReactNode } from "react";

export type TitleComponent = ComponentType<{
  className?: string;
  asChild?: boolean;
  children: ReactNode;
}>;
export type DescriptionComponent = ComponentType<{
  className?: string;
  asChild?: boolean;
  children: ReactNode;
}>;

/**
 * Recursively checks whether `Component` appears anywhere in `node`'s element
 * tree (including inside fragments and nested children), matched by
 * reference. Used to detect whether a render-prop callback actually rendered
 * a given component, without mounting anything.
 */
export const includesComponent = <P>(
  node: ReactNode,
  Component: ComponentType<P>,
): boolean => {
  let found = false;

  Children.forEach(node, (child) => {
    if (found || !isValidElement(child)) {
      return;
    }
    if (child.type === Component) {
      found = true;
      return;
    }
    found = includesComponent(
      (child.props as { children?: ReactNode }).children,
      Component,
    );
  });

  return found;
};

/** Radix's documented aria-describedby={undefined} opt-out, spread onto Content when no Description is rendered. */
export const describedByProps = (describedBy: boolean) =>
  describedBy ? {} : { "aria-describedby": undefined };
